import { MAX_RETRIES } from './MultipartUploadConstants';
import axios, { AxiosProgressEvent, AxiosResponse } from 'axios';

import {
    UploadChunk,
    UploadParts,
    ReadyUpload,
    ReadyUploadChunk,
    Upload,
} from './useMultipartUploadTypes';
import { XMLParser } from 'fast-xml-parser';
import logger from '@upheal/utils/logger';

export const isChunkEligibleForUpload = (
    chunk: UploadChunk,
): chunk is ReadyUploadChunk =>
    Boolean(
        chunk.state === 'idle' &&
            chunk.attempt <= MAX_RETRIES &&
            chunk.presignedUrl,
    );

export const doesChunkNeedPresignedUrl = (chunk: UploadChunk) =>
    chunk.state === 'idle' && !chunk.presignedUrl;

export const hasChunkUploadFailed = (chunk: UploadChunk) =>
    chunk.attempt > MAX_RETRIES;

export const isReadyUpload = (upload: Upload): upload is ReadyUpload => {
    return (
        upload.aws !== undefined &&
        upload.completePresignedUrl !== undefined &&
        upload.abortPresignedUrl !== undefined
    );
};

export const isUploadReadyToConclude = (
    upload: Upload,
): upload is ReadyUpload => {
    return (
        isReadyUpload(upload) &&
        upload.state === 'finishedWithCurrentChunks' &&
        upload.uploadMode === 'immediate'
    );
};

export const hasUploadConcludeFailed = (upload: ReadyUpload) =>
    upload.concludeAttempt > MAX_RETRIES;

export const uploadPart = ({
    data,
    uploadUrl,
    abortSignal,
    onUploadProgress,
}: {
    data: Blob;
    uploadUrl: string;
    abortSignal?: AbortSignal;
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
}) => {
    return axios.request({
        url: uploadUrl,
        method: 'put',
        data,
        signal: abortSignal,
        onUploadProgress,
    });
};

export const createConcludePartsXML = (parts: UploadParts) => {
    const xmlParts = parts
        .map((part) => {
            return `
                <Part>
                    <ETag>${part.etag}</ETag>
                    <PartNumber>${part.partNumber}</PartNumber>
                </Part>`;
        })
        .join('');

    const xml = `<?xml version="1.0" encoding="UTF-8"?>
                          <CompleteMultipartUpload xmlns="http://s3.amazonaws.com/doc/2006-03-01/">
                          ${xmlParts}
                          </CompleteMultipartUpload>`;

    return xml.replace(/\s+/g, ' ').trim();
};

export const concludeMultipartUploadRequest = ({
    parts,
    completePresignedUrl,
}: {
    parts: UploadParts;
    completePresignedUrl: string;
}) => {
    const xmlPayload = createConcludePartsXML(parts);

    logger.debug('[multipart-upload] conclude payload xml', { xmlPayload });

    return axios.request({
        url: completePresignedUrl,
        method: 'post',
        headers: {
            'Content-Type': 'application/xml',
        },
        data: xmlPayload,
    });
};

class S3ConcludeMultipartError extends Error {
    constructor(
        message: string,
        public Code: string,
        public RawXML: string,
        public RequestId?: string,
        public HostId?: string,
    ) {
        super(message);
        this.name = 'S3ConcludeMultipartError';
    }
}

const MAX_LOGGED_XML_LENGTH = 1000;

export const validateConcludeResponse = (
    response: AxiosResponse,
    uploadId: string,
) => {
    const isParsableResponse =
        response && response.data && typeof response.data === 'string';

    if (!isParsableResponse) {
        // Unexpected payload format, not a string

        throw new S3ConcludeMultipartError(
            'S3 response was not a string',
            'UnexpectedS3XMLFormat',
            response.data.toString().substring(0, MAX_LOGGED_XML_LENGTH),
        );
    }

    const xmlData = response.data;
    let parsedXml;

    try {
        const parser = new XMLParser({
            ignoreAttributes: false,
        });

        parsedXml = parser.parse(xmlData);
    } catch (xmlError: unknown) {
        // Failed to parse XML, treat as an S3 error

        throw new S3ConcludeMultipartError(
            `Failed to parse S3 response XML: ${
                xmlError instanceof Error ? xmlError?.message : 'Unknown error'
            }`,
            'XMLParseError',
            xmlData.substring(0, MAX_LOGGED_XML_LENGTH),
        );
    }

    if (!parsedXml) {
        // Unexpected empty result from parsing

        throw new S3ConcludeMultipartError(
            'S3 response XML has no content',
            'UnexpectedS3XMLFormat',
            xmlData?.substring(0, MAX_LOGGED_XML_LENGTH),
        );
    }

    if (parsedXml.Error) {
        // S3 returned an error embedded in the 2xx response's XML body

        throw new S3ConcludeMultipartError(
            parsedXml.Error?.Message || 'Unknown S3 error message',
            parsedXml.Error?.Code || 'UnknownS3ErrorCode',
            xmlData.substring(0, MAX_LOGGED_XML_LENGTH),
            parsedXml.Error?.RequestId,
            parsedXml.Error?.HostId,
        );
    }

    if (!parsedXml.CompleteMultipartUploadResult) {
        // S3 did not return the expected CompleteMultipartUploadResult in the XML

        throw new S3ConcludeMultipartError(
            'Unexpected S3 payload',
            'UnknownS3ErrorCode',
            xmlData.substring(0, MAX_LOGGED_XML_LENGTH),
        );
    }

    const result = parsedXml.CompleteMultipartUploadResult;

    logger.debug('[multipart-upload] S3 XML indicates successful completion', {
        uploadId,
        location: result.Location,
        bucket: result.Bucket,
        key: result.Key,
        eTag: result.ETag,
    });
};

export const abortMultipartUploadRequest = ({
    abortPresignedUrl,
}: {
    abortPresignedUrl: string;
}) => {
    return axios.request({
        url: abortPresignedUrl,
        method: 'delete',
    });
};

export const buildUploadUrl = ({
    bucket,
    key,
}: {
    bucket: string;
    key: string;
}) => {
    return `s3://${bucket}/${key}`;
};

// Chunks are 0 indexed, while parts for s3 are 1 indexed
export const chunkIndexToPartNumber = (index: number) => index + 1;

/**
 * Chunks are held in a ref, so we can mutate the individual objects in place, regardless
 * of whether we access them
 * - on the ref
 * - work with any shallow copies of the chunk array
 * - just the chunks themselves
 */
export const moveChunksToNewState = (
    chunks: Array<UploadChunk>,
    newState: UploadChunk['state'],
) => chunks.forEach((chunk) => (chunk.state = newState));

export const maskFilename = (filename: string) => {
    const parts = filename.split('.');
    const extension = parts.length > 1 ? parts.pop() : null;
    const name = parts.join('.');

    const visibleChars = 1;

    return (
        name.substring(0, visibleChars) +
        name
            .substring(visibleChars, name.length - visibleChars)
            .replace(/./g, '*') +
        name.substring(name.length - visibleChars, name.length) +
        (extension ? `.${extension}` : '')
    );
};
